import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as CheckIcon } from "../../../../../assects/svg/client_details/check-circle.svg";
import { ReactComponent as NoImageIcon } from "../../../../../assects/svg/client_details/no_images.svg";
import { MealData } from "../../../hooks/useAllLogsData";
import { BASE_URL } from "../../../../api/url";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";

interface AssignMealCardProps {
  mealData?: MealData[];
  mealPlanInfo?: {
    user_id: number;
    nutritionist_id: number;
    meal_plan_id: number;
    meal_plan_name: string;
    meal_plan_type: string;
    meal_plan_type_for_current_date: string;
    activation_date: string;
    past_meal_plan_type_activation_date: string;
    meal_plan_instructions: string;
  };
  selectedDate?: Date; // ✅ add this
}

const AssignMealCard: React.FC<AssignMealCardProps> = ({ mealData, mealPlanInfo, selectedDate }) => {
  const [selectedMealType, setSelectedMealType] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Set initial selected meal type when data loads
  useEffect(() => {
    if (mealData && mealData.length > 0 && !selectedMealType) {
      setSelectedMealType(mealData[0].user_meal_type);
    }
  }, [mealData, selectedMealType]);

  // Reset image index when meal type changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedMealType]);

  // Get the currently selected meal
  const selectedMeal = mealData?.find(
    (meal) => meal.user_meal_type === selectedMealType
  );

  // Get logged options if any
  const loggedOptions = selectedMeal?.log_options || [];

  // Check if the selected meal was logged as a custom/modified entry
  // Only show "Modified" when log_type is "Custom" but the meal type itself is NOT a custom meal type
  const isModified =
  selectedMeal?.is_custom_meal_type === false &&
  loggedOptions.some(
    (opt) => opt.log_type === "Custom" || opt.meal_plan_status === "2"
  );

  // Determine which data to show:
  // Only show food data if there are logged options — never fall back to meal plan template
  let displayData: NonNullable<typeof selectedMeal>["Options"] = [];
  if (loggedOptions.length > 0) {
    displayData = loggedOptions.flatMap(logOption => logOption.options || []);
  }

  // Get images from all logged options (different log sections)
  const mealImages: string[] = [];
  loggedOptions.forEach((logOption) => {
    if (logOption.images && logOption.images.length > 0) {
      const fullUrls = logOption.images.map((img) =>
        img.startsWith("http") ? img : `${BASE_URL}${img}`
      );
      mealImages.push(...fullUrls);
    }
  });

  // Only use images from API - no default images
  const imagesToDisplay = mealImages;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? imagesToDisplay.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === imagesToDisplay.length - 1 ? 0 : prev + 1
    );
  };

  const handleImageClick = () => {
    setIsFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreenOpen(false);
  };

  // Auto-scroll images every 4 seconds
  useEffect(() => {
    if (imagesToDisplay.length <= 1 || isPaused || isFullscreenOpen) return;

    const interval = setInterval(() => {
      handleNextImage();
    }, 4000);

    return () => clearInterval(interval);
  }, [currentImageIndex, imagesToDisplay.length, isPaused, isFullscreenOpen]);

  // Keyboard navigation for fullscreen
  useEffect(() => {
    if (!isFullscreenOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseFullscreen();
      } else if (e.key === "ArrowLeft") {
        handlePrevImage();
      } else if (e.key === "ArrowRight") {
        handleNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreenOpen, imagesToDisplay.length]);

  // Handle body scroll and animation state
  useEffect(() => {
    if (isFullscreenOpen) {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      document.body.style.overflow = "unset";
      if (isAnimating) {
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isFullscreenOpen, isAnimating]);

  // Helper function to parse 24-hour time string to 12-hour format with AM/PM
  const parseTime24To12 = (time24: string): { hour: number; minute: number; period: string } => {
    const [hourStr, minuteStr] = time24.split(':');
    const hour24 = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;

    const period = hour24 >= 12 ? 'PM' : 'AM';

    return { hour: hour12, minute, period };
  };

  // Helper function to parse logged_time (HH:MM:SS format)
  const parseLoggedTime = (loggedTime?: string): { hour: number; minute: number; period: string } | null => {
    if (!loggedTime) return null;

    try {
      const [hourStr, minuteStr] = loggedTime.split(':');
      const hour24 = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;

      const period = hour24 >= 12 ? 'PM' : 'AM';

      return { hour: hour12, minute, period };
    } catch (e) {
      console.error('Error parsing logged_time:', loggedTime, e);
      return null;
    }
  };

  // Format time display
  const formatTime = (hour: number, minute: number, period: string) => {
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  // Helper function to check if meal time has passed
  const isMealTimePassed = (meal: MealData): boolean => {
    const now = new Date();

    // Use selectedDate if provided, otherwise fall back to today
    const referenceDate = selectedDate ? new Date(selectedDate) : now;

    try {
      let hour24 = meal.meal_type_hour;
      if (meal.meal_type_period === "PM" && hour24 !== 12) hour24 += 12;
      if (meal.meal_type_period === "AM" && hour24 === 12) hour24 = 0;

      const mealDateTime = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate(),
        hour24,
        meal.meal_type_min
      );

      return now > mealDateTime;
    } catch (e) {
      console.error("Error parsing meal time:", e);
      return false;
    }
  };

  // Get button style based on status
  const getMealTypeButtonClass = (mealType: string) => {
    const isSelected = mealType === selectedMealType;
    const meal = mealData?.find((m) => m.user_meal_type === mealType);
    const hasLogs = meal && meal.log_options && meal.log_options.length > 0;

    const timePassed = meal ? isMealTimePassed(meal) : false;

    if (hasLogs) {
      return isSelected ? "bg-[#34C759] text-white" : "border-[0.5px] border-[#34C759] text-[#34C759]";
    }

    if (timePassed) {
      return isSelected ? "bg-[#FF9500] text-white" : "border-[0.5px] border-[#FF9500] text-[#FF9500]";
    }

    return isSelected ? "bg-[#3A3A3C] text-white" : "border-[0.5px] border-[#3A3A3C] text-[#3A3A3C]";
  };

  // ─── Empty Image Placeholder ───────────────────────────────────────────────
  const EmptyImagePlaceholder = () => (
    <div className="w-full h-full rounded-md bg-gray-100 flex items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        <NoImageIcon className="w-6 h-6 3xl:w-7 3xl:h-7" />
        <p className="text-xs text-gray-400 font-inter font-medium">
          No image added
        </p>
      </div>
    </div>
  );

  // ─── Render: No meal data at all ──────────────────────────────────────────
  if (!mealData || mealData.length === 0) {
    return (
      <div className="col-span-2 bg-white rounded-md shadow-sm p-4">
        <div className="flex flex-col items-center justify-center py-28">
          <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
        </div>
      </div>
    );
  }

  // ─── Render: Selected meal has no display data ────────────────────────────
  if (!selectedMeal || (!displayData || displayData.length === 0)) {
    let displayTime = '--:--';
    if (loggedOptions.length > 0) {
      const parsedLoggedTime = parseLoggedTime(loggedOptions[0].logged_time);
      if (parsedLoggedTime) {
        displayTime = formatTime(parsedLoggedTime.hour, parsedLoggedTime.minute, parsedLoggedTime.period);
      } else if (loggedOptions[0].hour !== undefined && loggedOptions[0].minute !== undefined && loggedOptions[0].period) {
        displayTime = formatTime(loggedOptions[0].hour, loggedOptions[0].minute, loggedOptions[0].period);
      }
    }

    return (
      <div className="col-span-2 bg-white rounded-md shadow-sm p-4">
        <div className="bg-white pb-3 border-b border-gray-200 mb-4">
          <div className="flex gap-2 overflow-x-auto custom-scrollbar-overlay pt-3 pb-2">
            {mealData.map((meal) => (
              <button
                key={meal.user_meal_type_id}
                onClick={() => setSelectedMealType(meal.user_meal_type)}
                className={`px-3 py-1.5 rounded-full text-xs 3xl:text-sm font-medium whitespace-nowrap ${getMealTypeButtonClass(
                  meal.user_meal_type
                )}`}
              >
                {meal.user_meal_type}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            {/* Meal Time Header */}
            <div className="flex items-center justify-between my-2 pb-2 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <h3 className="text-sm 3xl:text-base font-semibold text-[#1F1F1F] font-inter">
                  {mealPlanInfo?.meal_plan_type_for_current_date || "Regular"} - {displayTime}
                </h3>
                {selectedMeal?.is_custom_meal_type && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#E8F5E9] text-[#2E7D32] rounded">
                    Custom
                  </span>
                )}
                {loggedOptions.length > 0 && (
                  <CheckIcon className="w-5 h-5 3xl:w-6 3xl:h-6 flex-shrink-0" />
                )}
              </div>
              {isModified && (
                <span className="text-sm font-medium text-[#0BA5A4] font-inter">
                  Modified
                </span>
              )}
            </div>

            <div className="flex flex-col items-center justify-center py-24">
              <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
            </div>
          </div>

          {/* Empty Image Placeholder */}
          <div className="relative w-40 h-64 flex-shrink-0">
            <EmptyImagePlaceholder />
          </div>
        </div>
      </div>
    );
  }

  // ─── Parse meal time for display ─────────────────────────────────────────
  let mealTimeDisplay = '--:-- AM';
  if (selectedMeal?.meal_time) {
    const parsed = parseTime24To12(selectedMeal.meal_time);
    mealTimeDisplay = formatTime(parsed.hour, parsed.minute, parsed.period);
  }

  let displayTime = '--:--';
  if (loggedOptions.length > 0) {
    const parsedLoggedTime = parseLoggedTime(loggedOptions[0].logged_time);
    if (parsedLoggedTime) {
      displayTime = formatTime(parsedLoggedTime.hour, parsedLoggedTime.minute, parsedLoggedTime.period);
    } else if (loggedOptions[0].hour !== undefined && loggedOptions[0].minute !== undefined && loggedOptions[0].period) {
      displayTime = formatTime(loggedOptions[0].hour, loggedOptions[0].minute, loggedOptions[0].period);
    }
  }

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <>
      <div className="col-span-2 bg-white rounded-md shadow-sm p-4 min-w-0 overflow-hidden">
        <div className="bg-white pb-3 border-b border-gray-200 mb-4">
          <div className="flex gap-2 overflow-x-auto custom-scrollbar-overlay pt-3 pb-2">
            {mealData.map((meal) => (
              <button
                key={meal.user_meal_type_id}
                onClick={() => setSelectedMealType(meal.user_meal_type)}
                className={`px-3 py-1.5 rounded-full text-xs 3xl:text-sm font-medium whitespace-nowrap flex-shrink-0 ${getMealTypeButtonClass(
                  meal.user_meal_type
                )}`}
              >
                {meal.user_meal_type}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-4">
            {/* Meal Time Header */}
            <div className="flex items-center justify-between my-2 pb-2 border-b border-gray-200">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm 3xl:text-base font-semibold text-[#1F1F1F] font-inter truncate">
                  {mealPlanInfo?.meal_plan_type_for_current_date || "Regular"} - {displayTime}
                </h3>
                {selectedMeal?.is_custom_meal_type && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#E8F5E9] text-[#2E7D32] rounded flex-shrink-0">
                    Custom
                  </span>
                )}
                {loggedOptions.length > 0 && (
                  <CheckIcon className="w-5 h-5 3xl:w-6 3xl:h-6 flex-shrink-0" />
                )}
              </div>
              {isModified && (
                <span className="text-sm font-medium text-[#0BA5A4] font-inter flex-shrink-0 ml-2">
                  Modified
                </span>
              )}
            </div>

            {/* Food Items List */}
            <div className="max-h-48 overflow-y-auto pr-2 custom-scrollbar-overlay">
              {displayData.map((option, optionIndex) => (
                <div key={option.option_id}>
                  {option.food_data.map((food, foodIndex) => (
                    <div key={`${option.option_id}-${food.food_id}-${foodIndex}`} className="flex justify-between py-2">
                      <span className="text-sm 3xl:text-base font-medium text-[#1F1F1F] font-inter">
                        {food.item_name} - {food.measure_unit_type}
                      </span>
                      <div className="flex items-end">
                        <span className="text-sm 3xl:text-base font-medium text-[#1F1F1F] font-inter">
                          { food.quantity}
                        </span>
                        <span className="text-sm 3xl:text-base font-medium text-[#646464] font-inter">
                           ({food.base_unit_quantity} {food.base_unit_type})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Image Container */}
          <div className="relative w-40 h-64 flex-shrink-0">
            {imagesToDisplay.length > 0 ? (
              <div
                className="w-full h-full group"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <img
                  src={imagesToDisplay[currentImageIndex]}
                  className="w-full h-full rounded-md object-cover cursor-pointer transition-transform"
                  alt="Meal"
                  onClick={handleImageClick}
                />

                {/* Navigation Arrows - show on hover */}
                {imagesToDisplay.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Previous image"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Next image"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image Indicators */}
                {imagesToDisplay.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 ">
                    {imagesToDisplay.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        className={`h-1.5 rounded-full transition-all ${
                          index === currentImageIndex
                            ? "bg-white w-3"
                            : "bg-white/50 hover:bg-white/80 w-1.5"
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <EmptyImagePlaceholder />
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal - Rendered as Portal */}
      {imagesToDisplay.length > 0 &&
        (isFullscreenOpen || isAnimating) &&
        createPortal(
          <div className="image-fullscreen-container">
            {/* Backdrop */}
            <div
              className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
                isFullscreenOpen && isAnimating
                  ? "bg-opacity-95"
                  : "bg-opacity-0 pointer-events-none"
              }`}
              onClick={handleCloseFullscreen}
              style={{ zIndex: 9998, position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
            />

            {/* Modal Content */}
            <div
              className={`fixed inset-0 flex items-center justify-center p-4 transition-opacity duration-300 ease-in-out ${
                isFullscreenOpen && isAnimating
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              }`}
              style={{ zIndex: 9999, position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseFullscreen}
                className="absolute top-4 right-4 text-white hover:text-gray-300 text-4xl font-light w-12 h-12 flex items-center justify-center z-10 transition-colors"
                aria-label="Close fullscreen"
              >
                ×
              </button>

              {/* Image Counter */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
                {currentImageIndex + 1} / {imagesToDisplay.length}
              </div>

              {/* Main Image Container */}
              <div className="relative max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center">
                <img
                  src={imagesToDisplay[currentImageIndex]}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  alt={`Meal ${currentImageIndex + 1}`}
                  onClick={(e) => e.stopPropagation()}
                />

                {/* Navigation Arrows */}
                {imagesToDisplay.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all backdrop-blur-sm"
                      aria-label="Previous image"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all backdrop-blur-sm"
                      aria-label="Next image"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              {imagesToDisplay.length > 1 && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-2 bg-black/50 rounded-lg backdrop-blur-sm custom-scrollbar-overlay">
                  {imagesToDisplay.map((img, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden transition-all ${
                        index === currentImageIndex
                          ? "ring-2 ring-white scale-110"
                          : "opacity-50 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img}
                        className="w-full h-full object-cover"
                        alt={`Thumbnail ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AssignMealCard;